<template>
  <div class="combobox-rental-mutation-group-display">
    <div class="float-right flex w-fit items-center pl-1 text-right">
      <PBadgeRentalFlowDirection :flow-direction="flowDirection" />
    </div>
    <div class="space-x-1">
      <ComboboxRentalMutationOrRentalMutationItemDisplay
        :item="rentalIntakeOrRentalIntakeItem"
        :flow-direction="flowDirection"
        :show-project="showRentalIntakeProject"
        :show-date="showRentalIntakeDate"
        :show-badge="false"
        :show-counter-booking="false"
        :is-list-item="isListItem"
        :error="error[0]"
        @clear-error="$emit('clearError', 0)"
      />
      <ComboboxRentalMutationOrRentalMutationItemDisplay
        :item="rentalDispenseOrRentalDispenseItem"
        :flow-direction="flowDirection"
        :show-project="showRentalDispenseProject"
        :show-date="showRentalDispenseDate"
        :show-badge="false"
        :is-list-item="isListItem"
        @clear-error="$emit('clearError', 1)"
        :error="error[1]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type Ref } from "vue"
import { RentalIntakeItem } from "@/models/rental-intake-item.model"
import { RentalMutationFlowDirections } from "@/enums/rental-mutation-flow-directions"
import { RentalIntake } from "@/models/rental-intake.model"
import { RentalDispense } from "@/models/rental-dispense.model"
import ComboboxRentalMutationOrRentalMutationItemDisplay from "@/components/administration/combobox-display-templates/ComboboxRentalMutationOrRentalMutationItemDisplay.vue"
import { BaseException } from "@/exceptions/base-exception.exception"
import PBadgeRentalFlowDirection from "@/components/administration/PBadgeRentalFlowDirection.vue"
import type { Instruction } from "@/models/instruction.model.ts"

const props = withDefaults(
  defineProps<{
    item: RentalIntakeItem | [RentalIntake, RentalDispense] | RentalIntake
    flowDirection: RentalMutationFlowDirections
    isBeingUpdated?: boolean
    isListItem?: boolean
    error?: (BaseException | undefined)[]
  }>(),
  {
    error: () => [undefined, undefined]
  }
)

defineEmits(["clearError"])

const rentalIntakeOrRentalIntakeItem = computed(() => {
  if (props.item instanceof RentalIntakeItem) {
    // List item single issuance
    return props.item
  } else if (Array.isArray(props.item)) {
    // Selected item issuance and dispense, return issuance
    return props.item[0]
  }
  // List item return: rental intake
  return props.item
})

const rentalDispenseOrRentalDispenseItem = computed(() => {
  if (props.item instanceof RentalIntakeItem) {
    // List item single issuance
    return props.item.rentalDispenseItem
  } else if (Array.isArray(props.item)) {
    // Selected item issuance and dispense, return dispense
    return props.item[1]
  }
  // List item return: (associated) rental dispense
  return props.item.associatedRentalDispense!
})

// Only show date if the intake and dispense have different commencement dates
const showRentalIntakeDate = computed(() => {
  if (rentalIntakeOrRentalIntakeItem.value instanceof RentalIntake) {
    if (props.isListItem) {
      return true // Always show date for (returnable) list items because we want to know the date of the mutation.
    }
    return true // Always show date for rental intake (component will handle actual display based on instruction)
  }
  return false // Date is not applicable for rental intake items
})

const showRentalDispenseDate = computed(() => {
  if (rentalDispenseOrRentalDispenseItem.value instanceof RentalDispense) {
    if (props.isListItem) {
      // Only show date for (returnable) dispense list item if the intake and dispense have different commencement dates
      const intake = rentalIntakeOrRentalIntakeItem.value as RentalIntake
      const dispense = rentalDispenseOrRentalDispenseItem.value
      return !intake.commencementDate.isSame(dispense.commencementDate)
    }
    // Always show date for selected rental dispense (component will handle actual display based on instruction date)
    return true
  }
  return false // Date is not applicable for rental dispense items
})

const instruction = inject<Ref<Instruction>>("instruction")!
const showRentalIntakeProject = computed(() => {
  if (rentalIntakeOrRentalIntakeItem.value instanceof RentalIntake) {
    if (props.isListItem) {
      return true // Always show project for list items
    }

    const intake = rentalIntakeOrRentalIntakeItem.value
    // Show project if the intake and INSTRUCTION have different projects
    if (intake.flowDirection === RentalMutationFlowDirections.ISSUANCE) {
      return !intake.project || intake.project?.id !== instruction.value.project?.id
    } else {
      return intake.counterMutation!.project?.id !== instruction.value.project?.id
    }
  }
  // Project is not applicable for rental intake items
  return false
})

const showRentalDispenseProject = computed(() => {
  if (rentalDispenseOrRentalDispenseItem.value instanceof RentalDispense) {
    const intake = rentalIntakeOrRentalIntakeItem.value as RentalIntake
    const dispense = rentalDispenseOrRentalDispenseItem.value

    // Show project if the intake and DISPENSE have different projects. This way, the project is normally only shown
    // on the intake, indicating the same project is used for the dispense. But if the projects are different, the
    // project is shown on the dispense as well.
    if (dispense.flowDirection === RentalMutationFlowDirections.ISSUANCE) {
      return dispense.project?.id !== intake.project?.id
    } else {
      return dispense.counterMutation!.project?.id !== intake.counterMutation!.project?.id
    }
  }
  // Project is not applicable for rental dispense items
  return false
})
</script>

<style scoped></style>
